import { useCallback, useEffect, useRef, useState } from 'react'
import API from '../../../../lib/commons/api'
import Auth from '../../../../lib/commons/auth'
import { aiAgentConverse, PageContext } from '../../../../lib/functions/ai'
import { TokenStreamedPayload, combineTokenChunks } from '../tokenChunks'

export type AiAgentChatMessage = {
  // user / assistant のメッセージ。assistant はトークンを追記して成長する。
  role: 'user' | 'assistant'
  text: string
  isError?: boolean
  // ストリーミング完了済みかどうか。
  complete?: boolean
}

// STOMP の通知ペイロード（トークンは tokenChunks.ts の TokenStreamedPayload）。
type TurnCompletedPayload = { conversationUuid: string; stopReason: string }
type AgentErrorPayload = {
  conversationUuid: string
  code: string
  message: string
}

// 同一会話でストリーミング中のターンが存在する場合、converse は 409 を返す。
const isConversationBusyError = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  'status' in error &&
  error.status === 409

export type UseAiAgentConversationInput = {
  greetingText: string
  // 同一会話でストリーミング中に再送信した場合（409）の通知文言。
  busyErrorText: string
  // converse 呼び出し自体が失敗した場合の文言。
  generalErrorText: string
  // ストリーミング中にエージェント側でエラーが発生した場合の既定文言。
  streamErrorText: string
}

/**
 * AIエージェントとのマルチターン会話（converse 202 + STOMPストリーミング）を管理するフック。
 * フローティングパネル・専用ページの双方から利用するため、UI文言は呼び出し側から注入する。
 */
export const useAiAgentConversation = ({
  greetingText,
  busyErrorText,
  generalErrorText,
  streamErrorText,
}: UseAiAgentConversationInput) => {
  const greeting: AiAgentChatMessage = {
    role: 'assistant',
    text: greetingText,
    complete: true,
  }
  const [messages, setMessages] = useState<AiAgentChatMessage[]>([greeting])
  const [isStreaming, setIsStreaming] = useState(false)
  const [inlineError, setInlineError] = useState<string | undefined>(undefined)

  // 会話をまたいで安定させる conversationUuid。初回ターンは null（未指定）で送信し、
  // 202 で返ってきた値を保持して以降のターンで再利用する（マルチターン）。
  const conversationUuidRef = useRef<string | undefined>(undefined)
  // 受信したトークンを index 順にバッファリングし、結合してアシスタント末尾メッセージに反映する。
  // バッファはターン開始時にリセットする（index はターンごとに 0 から振り直される）。
  const tokenBufferRef = useRef<TokenStreamedPayload[]>([])
  // 現在 STOMP 購読済みの conversationUuid。同一会話の 2 回目以降ターンで再購読しないためのガード。
  const subscribedConversationUuidRef = useRef<string | undefined>(undefined)
  // アンマウント時に購読を解除するためにトピック一覧を保持する。
  const subscribedTopicsRef = useRef<string[]>([])

  const unsubscribeActive = useCallback(() => {
    subscribedTopicsRef.current.forEach(topic =>
      API.notification.unsubscribe(topic)
    )
    subscribedTopicsRef.current = []
    subscribedConversationUuidRef.current = undefined
  }, [])

  // バッファされたトークンを index 順に結合し、末尾アシスタントメッセージへ反映する。
  const flushAssistantText = useCallback(() => {
    const text = combineTokenChunks(tokenBufferRef.current)
    setMessages(prev => {
      const next = [...prev]
      for (let i = next.length - 1; i >= 0; i--) {
        if (next[i].role === 'assistant' && !next[i].complete) {
          next[i] = { ...next[i], text }
          break
        }
      }
      return next
    })
  }, [])

  const markAssistantComplete = useCallback((errorText?: string) => {
    setMessages(prev => {
      const next = [...prev]
      for (let i = next.length - 1; i >= 0; i--) {
        if (next[i].role === 'assistant' && !next[i].complete) {
          next[i] = {
            ...next[i],
            complete: true,
            ...(errorText !== undefined
              ? { text: errorText, isError: true }
              : {}),
          }
          break
        }
      }
      return next
    })
  }, [])

  const endTurn = useCallback(() => {
    setIsStreaming(false)
    // ターン完了時は購読を維持する（同一 conversationUuid でマルチターンを継続するため）。
    // 購読解除はリセット時またはアンマウント時に行う。
  }, [])

  // 202 で確定した conversationUuid に対してストリーミングトピックを購読する。
  // 同一 conversationUuid に対しては一度だけ購読し、マルチターン全体で購読を維持する。
  // 既知の制限: バックエンドはこの購読より前にトークンを publish しうる（理論上の取りこぼし）。
  // バックエンドの最初の flush は invoke 開始から 150ms 以上、加えてエージェントのコールドスタートが
  // あるため、202 受信直後に購読する本実装で MVP としては許容できるレースである。
  const subscribeToConversation = useCallback(
    (conversationUuid: string) => {
      // 既に同一会話の購読が存在する場合は再購読しない（重複購読によるトークン多重配信を防ぐ）。
      if (subscribedConversationUuidRef.current === conversationUuid) {
        return
      }
      const tenant = Auth.getCurrentTenant()
      if (!tenant) {
        return
      }
      const tenantUuid = tenant.tenantUuid
      const tokenTopic = `${tenantUuid}/AiAgentTokenStreamed/${conversationUuid}`
      const completedTopic = `${tenantUuid}/AiAgentTurnCompleted/${conversationUuid}`
      const errorTopic = `${tenantUuid}/AiAgentError/${conversationUuid}`

      API.notification.subscribe<TokenStreamedPayload>(tokenTopic, payload => {
        if (conversationUuidRef.current !== conversationUuid) {
          return
        }
        tokenBufferRef.current.push(payload)
        flushAssistantText()
      })
      API.notification.subscribe<TurnCompletedPayload>(
        completedTopic,
        payload => {
          if (payload.conversationUuid !== conversationUuid) {
            return
          }
          markAssistantComplete()
          endTurn()
        }
      )
      API.notification.subscribe<AgentErrorPayload>(errorTopic, payload => {
        if (payload.conversationUuid !== conversationUuid) {
          return
        }
        markAssistantComplete(payload.message || streamErrorText)
        endTurn()
      })

      subscribedConversationUuidRef.current = conversationUuid
      subscribedTopicsRef.current = [tokenTopic, completedTopic, errorTopic]
    },
    [flushAssistantText, markAssistantComplete, endTurn, streamErrorText]
  )

  const sendMessage = useCallback(
    // pageContext は送信時点の画面状態を反映する必要があるため（設計文書のとおりターンごとに
    // 変わりうる）、フックの入力ではなく sendMessage の呼び出し時引数として受け取る。
    async (text: string, pageContext?: PageContext) => {
      if (isStreaming) {
        return
      }
      const messageText = text.trim()
      if (!messageText) {
        return
      }

      setInlineError(undefined)
      setIsStreaming(true)
      // 新しいターン用にトークンバッファをリセットし、ユーザー発言と空のアシスタント枠を追加する。
      tokenBufferRef.current = []
      setMessages(prev => [
        ...prev,
        { role: 'user', text: messageText },
        { role: 'assistant', text: '' },
      ])

      try {
        const response = await aiAgentConverse({
          prompt: messageText,
          conversationUuid: conversationUuidRef.current,
          pageContext,
        })
        conversationUuidRef.current = response.conversationUuid
        // 202 受信直後に購読する（トークンの取りこぼしを最小化）。
        subscribeToConversation(response.conversationUuid)
      } catch (error) {
        if (isConversationBusyError(error)) {
          setInlineError(busyErrorText)
          // 409 の場合は空のアシスタント枠を残さない。
          setMessages(prev => {
            const next = [...prev]
            const last = next[next.length - 1]
            if (last && last.role === 'assistant' && last.text === '') {
              next.pop()
            }
            return next
          })
        } else {
          markAssistantComplete(generalErrorText)
        }
        setIsStreaming(false)
      }
    },
    [
      isStreaming,
      subscribeToConversation,
      markAssistantComplete,
      busyErrorText,
      generalErrorText,
    ]
  )

  const resetConversation = useCallback(() => {
    if (isStreaming) {
      return
    }
    unsubscribeActive()
    conversationUuidRef.current = undefined
    tokenBufferRef.current = []
    setMessages([{ role: 'assistant', text: greetingText, complete: true }])
    setInlineError(undefined)
  }, [isStreaming, unsubscribeActive, greetingText])

  // アンマウント時に購読を解除する。
  useEffect(() => {
    return () => {
      unsubscribeActive()
    }
  }, [unsubscribeActive])

  return {
    messages,
    isStreaming,
    inlineError,
    sendMessage,
    resetConversation,
  }
}
